import { Component, ViewChild, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, merge, startWith, switchMap, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { RbacService } from 'src/app/private/services/rbac.service';
import { SubscriptionService } from 'src/app/private/services/subscription.service';
import { SIDEBAR_CONFIG } from 'src/app/private/services/module.config';

@Component({
  selector: 'app-role-based-access-control',
  templateUrl: './role-based-access-control.component.html',
  styleUrls: ['./role-based-access-control.component.scss']
})
export class RoleBasedAccessControlComponent implements OnInit, AfterViewInit, OnDestroy {

  isLoading = false;
  initialLoad = false;
  totalRows = 0;
  pageSize = 10;
  currentPage = 0;
  pageSizeOptions: number[] = [10, 25, 100];
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  filterArray: any[] = [];

  private destroy$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("roleForm") roleForm!: NgForm;

  searchQuery = new FormControl();
  displayedColumns: string[] = ["role_name", "description", "total_permissions", "status", "action"];

  role: Role = new Role();
  formAction = 'add';
  isSubmitted = false;
  deleteParam: any = null;

  groupedPermissions: { [key: string]: PermissionGroup } = {};
  selectedPermissions: { [key: string]: boolean } = {};
  enabledModules: { [key: string]: boolean } = {}; // Track module enable/disable state

  allowedFeatures: Set<string> = new Set();
  subscriptionFeatures: any[] = [];

  // 🎯 PREDEFINED ROLE TEMPLATES
  roleTemplates = [
    {
      name: 'Sales Manager',
      key: 'SALES_MANAGER',
      description: 'Full access to sales modules with team management capabilities',
      icon: 'fas fa-user-tie',
      color: '#3b82f6',
      permissions: {
        lead: { enabled: true, actions: ['lead_create', 'lead_read', 'lead_update', 'lead_delete', 'lead_export', 'lead_import', 'lead_filter'], features: ['lead_scoring', 'lead_assignment', 'communication', 'activity_log', 'task', 'notes', 'meetings', 'lead_meeting_organizer'] },
        contacts: { enabled: true, actions: ['contacts_create', 'contacts_read', 'contacts_update', 'contacts_delete', 'contacts_export', 'contacts_import'], features: [] },
        account: { enabled: true, actions: ['account_create', 'account_read', 'account_update', 'account_delete', 'account_export'], features: [] },
        deals: { enabled: true, actions: ['deals_create', 'deals_read', 'deals_update', 'deals_delete', 'deals_export'], features: ['deal_stages'] },
        analytics: { enabled: true, actions: ['analytics_view', 'analytics_export'], features: ['lead_analytics', 'deal_analytics', 'revenue_analytics'] },
        forecast: { enabled: true, actions: ['forecast_read', 'forecast_create', 'forecast_update'], features: [] }
      }
    },
    {
      name: 'Sales Representative',
      key: 'SALES_REPRESENTATIVE',
      description: 'Basic sales access with limited administrative permissions',
      icon: 'fas fa-user',
      color: '#10b981',
      permissions: {
        lead: { enabled: true, actions: ['lead_create', 'lead_read', 'lead_update', 'lead_filter'], features: ['communication', 'activity_log', 'task', 'notes', 'meetings', 'lead_meeting_organizer'] },
        contacts: { enabled: true, actions: ['contacts_create', 'contacts_read', 'contacts_update'], features: [] },
        account: { enabled: true, actions: ['account_read'], features: [] },
        deals: { enabled: true, actions: ['deals_create', 'deals_read', 'deals_update'], features: [] },
        analytics: { enabled: true, actions: ['analytics_view'], features: ['lead_analytics'] }
      }
    },
    {
      name: 'Marketing Manager',
      key: 'MARKETING_MANAGER',
      description: 'Full access to marketing and campaign modules',
      icon: 'fas fa-bullhorn',
      color: '#8b5cf6',
      permissions: {
        campaign: { enabled: true, actions: ['campaign_create', 'campaign_read', 'campaign_update', 'campaign_delete', 'campaign_export'], features: ['email_campaign', 'whatsapp_campaign'] },
        sequence: { enabled: true, actions: ['sequence_create', 'sequence_read', 'sequence_update', 'sequence_delete'], features: [] },
        workflow_automation: { enabled: true, actions: ['workflow_automation_create', 'workflow_automation_read', 'workflow_automation_update', 'workflow_automation_delete'], features: [] },
        lead: { enabled: true, actions: ['lead_read', 'lead_filter', 'lead_export'], features: ['lead_scoring', 'communication'] },
        contacts: { enabled: true, actions: ['contacts_read', 'contacts_export', 'contacts_import'], features: [] },
        analytics: { enabled: true, actions: ['analytics_view', 'analytics_export'], features: ['lead_analytics'] },
        setting: { enabled: true, actions: [], features: ['email_template', 'whatsapp_template', 'contact_group'] }
      }
    },
    {
      name: 'Customer Support',
      key: 'SUPPORT_AGENT',
      description: 'Access to support tickets and customer communication',
      icon: 'fas fa-headset',
      color: '#f59e0b',
      permissions: {
        tickets: { enabled: true, actions: ['tickets_create', 'tickets_read', 'tickets_update', 'tickets_export'], features: [] },
        support: { enabled: true, actions: ['support_create', 'support_read', 'support_update', 'support_export'], features: [] },
        contacts: { enabled: true, actions: ['contacts_read', 'contacts_update'], features: [] },
        account: { enabled: true, actions: ['account_read'], features: [] },
        lead: { enabled: true, actions: ['lead_read'], features: ['communication', 'notes'] }
      }
    },
    {
      name: 'Administrator',
      key: 'ADMINISTRATOR',
      description: 'Full system access including settings and user management',
      icon: 'fas fa-user-shield',
      color: '#ef4444',
      permissions: {
        // This will be populated with ALL available permissions
        _selectAll: true
      }
    },
    {
      name: 'Read Only',
      key: 'read_only',
      description: 'View-only access to all modules without edit permissions',
      icon: 'fas fa-eye',
      color: '#6b7280',
      permissions: {
        lead: { enabled: true, actions: ['lead_read'], features: ['activity_log'] },
        contacts: { enabled: true, actions: ['contacts_read'], features: [] },
        account: { enabled: true, actions: ['account_read'], features: [] },
        deals: { enabled: true, actions: ['deals_read'], features: [] },
        analytics: { enabled: true, actions: ['analytics_view'], features: ['lead_analytics', 'deal_analytics'] },
        tickets: { enabled: true, actions: ['tickets_read'], features: [] },
        support: { enabled: true, actions: ['support_read'], features: [] },
        workflow_automation: { enabled: true, actions: ['workflow_automation_read'], features: [] }
      }
    }
  ];

  selectedTemplate: string = '';

  constructor(
    private tracker: TrackingService,
    private toast: ToastrService,
    private api: RbacService,
    private modalService: NgbModal,
    private localStorage: LocalStorageService,
    private subscriptionService: SubscriptionService
  ) {}

  ngOnInit(): void {
    this.loadActivePackage();
  }

  ngAfterViewInit() {
    if (this.sort && this.paginator) {
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ================= PACKAGE LOAD ================= */

  loadActivePackage() {
    const payload = { client_id: this.localStorage.getClientId() };

    this.subscriptionService.getCurrentPackageFeatureAccess(payload).subscribe({
      next: (res) => {
        this.allowedFeatures.clear();
        this.subscriptionFeatures = [];

        if (res?.data?.features) {
          this.collectAllowedFeatures(res.data.features);
          this.flattenAndStoreFeatures(res.data.features);
        }

        this.generatePermissionsFromApi(res.data.features);
      },
      error: () => this.generatePermissionsFromApi([])
    });
  }

  private collectAllowedFeatures(features: any[]) {
    const traverse = (f: any) => {
      if (f.feature_key) {
        this.allowedFeatures.add(f.feature_key.toLowerCase());
      }
      if (f.children?.length) {
        f.children.forEach((c: any) => traverse(c));
      }
    };
    features.forEach(f => traverse(f));
  }

  private flattenAndStoreFeatures(features: any[]) {
    const traverse = (f: any) => {
      this.subscriptionFeatures.push({
        feature_key: f.feature_key,
        feature_level: f.feature_level || 'module_feature', // Store feature_level from API
        actions: f.actions || []
      });
      if (f.children?.length) {
        f.children.forEach((c: any) => traverse(c));
      }
    };
    features.forEach(f => traverse(f));
  }

  /* ================= PERMISSION GENERATION ================= */

  generatePermissionsFromSidebar() {
    this.groupedPermissions = {};

    const flattenChildren = (items: any[] = []): any[] => {
      const result: any[] = [];
      items.forEach((child) => {
        result.push(child);
        if (child.children?.length) {
          result.push(...flattenChildren(child.children));
        }
      });
      return result;
    };

    const walkModules = (items: any[]) => {
      items.forEach((moduleItem) => {
        const moduleKey = moduleItem.module?.toLowerCase();
        if (moduleKey && this.allowedFeatures.has(moduleKey) && !this.groupedPermissions[moduleKey]) {
          const moduleActions = this.getModuleActions(moduleKey);
          const features: UiPermission[] = [];

          flattenChildren(moduleItem.children).forEach((child) => {
            const featureKey = child.module?.toLowerCase();
            if (!featureKey || featureKey === moduleKey || !this.allowedFeatures.has(featureKey)) {
              return;
            }
            if (features.some((feature) => feature.key === featureKey)) {
              return;
            }

            const featureActions = this.getFeatureActions(featureKey);
            const featureData = this.subscriptionFeatures.find((f) =>
              f.feature_key?.toLowerCase() === featureKey.toLowerCase()
            );
            const featureLevel = featureData?.feature_level || 'module_feature';
            const featureLevelLabel = this.getFeatureLevelLabel(featureLevel);

            features.push({
              key: featureKey,
              label: child.label,
              module: moduleKey,
              isFeature: true,
              level: featureLevel,
              featureLevelLabel: featureLevelLabel,
              actions: featureActions
            });
          });

          this.groupedPermissions[moduleKey] = {
            module: moduleKey,
            moduleLabel: moduleItem.label,
            level: 'module',
            levelLabel: 'Parent Level',
            actions: moduleActions,
            features: features
          };
        }

        if (moduleItem.children?.length) {
          walkModules(moduleItem.children);
        }
      });
    };

    walkModules(SIDEBAR_CONFIG);
  }


  generatePermissionsFromApi(features: any[]) {
  this.groupedPermissions = {};

  features.forEach((moduleFeature: any) => {

    if (moduleFeature.status !== "Active") return;

    const moduleKey = moduleFeature.feature_key?.toLowerCase();
    if (!moduleKey) return;

    const moduleActions: PermissionAction[] =
      (moduleFeature.actions || []).map((action: string) => ({
        key: `${moduleKey}_${action}`,
        label: this.formatActionLabel(action),
        actionName: action
      }));

    const featureList: UiPermission[] = [];

    moduleFeature.children?.forEach((child: any) => {

      if (child.status !== "Active") return;

      const featureKey = child.feature_key?.toLowerCase();
      if (!featureKey) return;

      const featureActions: PermissionAction[] =
        (child.actions || []).map((action: string) => ({
          key: `${featureKey}_${action}`,
          label: this.formatActionLabel(action),
          actionName: action
        }));

      featureList.push({
        key: featureKey,
        label: child.label, // ✅ Direct from API
        module: moduleKey,
        isFeature: true,
        level: child.feature_level || 'module_feature',
        featureLevelLabel: this.getFeatureLevelLabel(child.feature_level),
        actions: featureActions
      });
    });

    this.groupedPermissions[moduleKey] = {
      module: moduleKey,
      moduleLabel: moduleFeature.label, // ✅ Direct from API
      level: 'module',
      levelLabel: 'Parent Level',
      actions: moduleActions,
      features: featureList
    };
  });

  this.ensureMeetingOrganizerFeature();
  this.ensureWorkflowAutomationModule();
}

  private ensureMeetingOrganizerFeature() {
    const leadGroup = Object.values(this.groupedPermissions).find(
      (group) => group.module === 'lead'
    );
    if (!leadGroup) return;

    const alreadyPresent = leadGroup.features?.some(
      (feature) => feature.key === 'lead_meeting_organizer'
    );
    if (alreadyPresent) return;

    leadGroup.features = leadGroup.features || [];
    leadGroup.features.push({
      key: 'lead_meeting_organizer',
      label: 'Meeting Organizer',
      module: 'lead',
      isFeature: true,
      level: 'module_feature',
      featureLevelLabel: this.getFeatureLevelLabel('module_feature'),
      actions: []
    });
  }

  /** Own role module, with the same create / read / update / delete actions as the other modules. */
  private ensureWorkflowAutomationModule() {
    const actions = ['create', 'read', 'update', 'delete'].map((action) => ({
      key: `workflow_automation_${action}`,
      label: this.formatActionLabel(action),
      actionName: action,
    }));
    const existing = this.groupedPermissions['workflow_automation'];
    if (existing) {
      if (!existing.actions?.length) existing.actions = actions;
      return;
    }
    this.groupedPermissions['workflow_automation'] = {
      module: 'workflow_automation',
      moduleLabel: 'Workflow Automation',
      level: 'module',
      levelLabel: 'Parent Level',
      actions,
      features: [],
    };
  }

private getSidebarLabel(featureKey: string): string {

  const target = featureKey?.toLowerCase();

  const findItem = (items: any[]): any | null => {
    for (const item of items) {

      // ✅ Match using module (API feature_key)
      if (item.module?.toLowerCase() === target) {
        return item;
      }

      // ✅ Fallback match using key
      if (item.key?.toLowerCase() === target) {
        return item;
      }

      // ✅ Search in children
      if (item.children?.length) {
        const found = findItem(item.children);
        if (found) return found;
      }
    }
    return null;
  };

  const match = findItem(SIDEBAR_CONFIG);

  return match?.label || featureKey;
}


  /**
   * Get actions for a module (parent level)
   * These are the standard CRUD + module-specific actions
   */
  private getModuleActions(moduleKey: string): PermissionAction[] {
    const featureData = this.subscriptionFeatures.find(f =>
      f.feature_key?.toLowerCase() === moduleKey.toLowerCase()
    );

    if (!featureData?.actions?.length) {
      return [];
    }

    return featureData.actions.map((action: string) => ({
      key: `${moduleKey}_${action}`,
      label: this.formatActionLabel(action),
      actionName: action
    }));
  }

  /**
   * Get actions for a feature (child level)
   * These are feature-specific actions
   */
  private getFeatureActions(featureKey: string): PermissionAction[] {
    const featureData = this.subscriptionFeatures.find(f =>
      f.feature_key?.toLowerCase() === featureKey.toLowerCase()
    );

    if (!featureData?.actions?.length) {
      return [];
    }

    return featureData.actions.map((action: string) => ({
      key: `${featureKey}_${action}`,
      label: this.formatActionLabel(action),
      actionName: action
    }));
  }

  /**
   * Format action name for display (e.g., 'create' -> 'Create')
   */
  private formatActionLabel(action: string): string {
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Get display label for feature level
   * Converts 'module' or 'module_feature' to readable format
   */
  private getFeatureLevelLabel(level: string | undefined): string {
    if (level === 'module') {
      return 'Module';
    } else if (level === 'module_feature') {
      return 'Module Feature';
    }
    return 'Feature Level';
  }

  saveRole(isValid: boolean) {
  this.isSubmitted = true;
  if (!isValid) return;

  this.isLoading = true;

  const modulesMap: { [key: string]: any } = {};

  Object.keys(this.groupedPermissions).forEach(groupLabel => {
    const group = this.groupedPermissions[groupLabel];
    const moduleName = group.module;
    const isEnabled = this.isModuleEnabled(moduleName);

    modulesMap[moduleName] = {
      module_name: moduleName,
      active: isEnabled,
      features: [],
      actions: []
    };

    if (!isEnabled) {
      return;
    }

    // Save module-level actions
    group.actions?.forEach(action => {
      if (this.selectedPermissions[action.key]) {
        modulesMap[moduleName].actions.push({
          name: action.key,
          active: true
        });
      }
    });

    // Save features + feature actions
    group.features?.forEach(feature => {
      if (this.selectedPermissions[feature.key]) {
        modulesMap[moduleName].features.push({
          name: feature.key,
          active: true
        });

        feature.actions?.forEach(action => {
          if (this.selectedPermissions[action.key]) {
            modulesMap[moduleName].actions.push({
              name: action.key,
              active: true
            });
          }
        });
      }
    });
  });

  // ✅ FINAL PAYLOAD (no filtering anymore)
  const permissionsPayload = Object.values(modulesMap);

  this.role.permissions = permissionsPayload;
  this.role.client_id = parseInt(this.localStorage.getClientId());

  console.log('💾 Final Payload:', JSON.stringify(permissionsPayload, null, 2));

  const request$ = this.formAction === 'add'
    ? this.api.createRole(this.role)
    : this.api.updateRole(this.role);

  request$.subscribe({
    next: () => {
      this.isLoading = false;
      this.modalService.dismissAll();
      this.toast.success(
        this.formAction === 'add'
          ? 'Role Added Successfully'
          : 'Role Updated Successfully',
        'Success'
      );
      this.loadData();
    },
    error: (err) => {
      this.isLoading = false;
      this.toast.error(err?.error?.msg || 'Error saving role', 'Error');
    }
  });
}



  /* ================= TABLE ================= */

  loadData() {
    this.initialLoad = true;

    merge(this.searchQuery.valueChanges, this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          let param = {
            where: { client_id: parseInt(this.localStorage.getClientId()) },
            page: (this.paginator.pageIndex * this.paginator.pageSize),
            limit: this.paginator.pageSize,
            filter: this.searchQuery.value,
          };
          return this.api.getAllRoles(param).pipe(catchError(() => of(null)));
        }),
        map((response: any) => {
          if (!response) return [];
          let rows = Array.isArray(response) ? response : response.rows || [];
          this.totalRows = Array.isArray(response) ? response.length : response.count || 0;
          return this.sortRolesByRecent(rows);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.initialLoad = false;
        this.dataSource = new MatTableDataSource<any>(data);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
      });
  }

  refreshData() {
    this.paginator.pageIndex = 0;
    this.searchQuery.reset();
    this.loadData();
  }

  searchFilter(event: any) {
    this.searchQuery.patchValue(event);
  }

  open(content: any) {
    this.resetForm();
    this.formAction = 'add';

    Object.values(this.groupedPermissions).forEach((group) => {
      this.enabledModules[group.module] = false;
    });

    const keys = this.objectKeys(this.groupedPermissions);
    if (keys.length > 0) {
      this.activeModule = keys[0];
    }

    this.modalService.open(content, { size: 'lg' });
  }

  deleteRoleModal(content: any, element: any) {
    this.deleteParam = element;
    this.modalService.open(content);
  }

  deleteRole() {
    if (!this.deleteParam) return;

    this.isLoading = true;
    this.api.deleteRole(this.deleteParam.role_id).subscribe({
      next: () => {
        this.isLoading = false;
        this.modalService.dismissAll();
        this.toast.success('Role Deleted Successfully', 'Success');
        this.loadData();
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error(err?.error?.msg || 'Error deleting role', 'Error');
      }
    });
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  getPermissionCount(element: any): number {
    if (!element.permissions || !Array.isArray(element.permissions)) return 0;
    return element.permissions.reduce((count: number, perm: any) => {
      return count + (perm.features?.filter((f: any) => f.active).length || 0);
    }, 0);
  }

  /** Show most recently updated/created roles first. */
  private sortRolesByRecent(rows: any[]): any[] {
    return [...rows].sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
      if (bTime !== aTime) {
        return bTime - aTime;
      }
      return (b.role_id || 0) - (a.role_id || 0);
    });
  }

  /**
   * Check if all module-level actions are selected
   */
  isModuleFullySelected(group: PermissionGroup): boolean {
    if (!group.actions || group.actions.length === 0) return false;
    return group.actions.every(action => this.selectedPermissions[action.key]);
  }

  /**
   * Check if all features in a module are selected
   */
  areFeaturesFullySelected(group: PermissionGroup): boolean {
    if (!group.features || group.features.length === 0) return false;
    return group.features.every(feature => this.selectedPermissions[feature.key]);
  }

  /**
   * Toggle all module-level actions
   */
  toggleModuleActions(group: PermissionGroup, event: any) {
    const isChecked = event.target.checked;
    group.actions?.forEach(action => {
      this.selectedPermissions[action.key] = isChecked;
    });
  }

  /**
   * Toggle all features in a module
   */
  toggleAllFeatures(group: PermissionGroup, event: any) {
    const isChecked = event.target.checked;
    group.features?.forEach(feature => {
      this.selectedPermissions[feature.key] = isChecked;
      // Also toggle all actions for this feature
      feature.actions?.forEach(action => {
        this.selectedPermissions[action.key] = isChecked;
      });
    });
  }

  /**
   * Toggle a single feature and its actions
   */
  toggleFeature(feature: UiPermission, event: any) {
    const isChecked = event.target.checked;
    this.selectedPermissions[feature.key] = isChecked;
    // Auto-toggle all actions when feature is selected/deselected
    feature.actions?.forEach(action => {
      this.selectedPermissions[action.key] = isChecked;
    });
  }

  /**
   * Toggle a single action
   */
  toggleAction(action: PermissionAction) {
    this.selectedPermissions[action.key] = !this.selectedPermissions[action.key];
  }

  /**
   * Toggle entire module enable/disable
   * When module is disabled, all its actions/features are automatically disabled
   */
  toggleModuleEnable(moduleKey: string, event: any) {
    const isEnabled = event.target.checked;
    this.enabledModules[moduleKey] = isEnabled;

    if (!isEnabled) {
      const group = Object.values(this.groupedPermissions).find(
        (g) => g.module === moduleKey,
      );
      group?.actions?.forEach((action) => {
        this.selectedPermissions[action.key] = false;
      });
      group?.features?.forEach((feature) => {
        this.selectedPermissions[feature.key] = false;
        feature.actions?.forEach((action) => {
          this.selectedPermissions[action.key] = false;
        });
      });
    }
  }

  /**
   * Check if a module is enabled
   */
  isModuleEnabled(moduleKey: string): boolean {
    return this.enabledModules[moduleKey] === true;
  }

  private resetForm() {
    this.role = new Role();
    this.selectedPermissions = {};
    this.enabledModules = {};
    this.isSubmitted = false;
    this.deleteParam = null;
  }

  /* ================= EDIT FUNCTION ================= */

  activeModule: string | null = null;

  edit(content: any, element: any) {
    this.formAction = 'edit';
    this.role = { ...element };
    this.selectedPermissions = {};
    this.enabledModules = {};

    Object.values(this.groupedPermissions).forEach((group) => {
      this.enabledModules[group.module] = false;
    });

    if (element.permissions && Array.isArray(element.permissions)) {
      element.permissions.forEach((modulePerm: any) => {
        const moduleName = modulePerm.module_name;

        this.enabledModules[moduleName] = modulePerm.active !== false;

        modulePerm.actions?.forEach((action: any) => {
          if (action.active) {
            this.selectedPermissions[action.name] = true;
          }
        });

        modulePerm.features?.forEach((feature: any) => {
          if (feature.active) {
            this.selectedPermissions[feature.name] = true;
          }
        });
      });
    }

    const keys = this.objectKeys(this.groupedPermissions);
    if (keys.length > 0) {
      this.activeModule = keys[0];
    }

    this.modalService.open(content, { size: 'lg' });
  }

  // Helper to safely get group by key
  getGroup(key: string): PermissionGroup {
    return this.groupedPermissions[key];
  }


  /* ================= ROLE TEMPLATE APPLICATION ================= */

  /**
   * Apply a predefined role template
   * Automatically selects permissions based on the template
   */
  applyRoleTemplate(templateKey: string) {
    const template = this.roleTemplates.find(t => t.key === templateKey);
    if (!template) return;

    // Set role name and description from template
    this.role.role_name = template.name;
    this.role.role_key = template.key;
    this.role.description = template.description;

    // Clear existing selections
    this.selectedPermissions = {};
    this.enabledModules = {};

    // Check if this is "select all" template (Administrator)
    if (template.permissions._selectAll) {
      this.selectAllPermissions();
      return;
    }

    // Apply template permissions
    Object.keys(template.permissions).forEach(moduleKey => {
      const modulePermissions = (template.permissions as any)[moduleKey];
      
      // Skip the _selectAll flag
      if (moduleKey === '_selectAll') return;
      
      // Enable the module
      if (modulePermissions.enabled) {
        this.enabledModules[moduleKey] = true;
      }

      // Select module-level actions
      modulePermissions.actions?.forEach((actionKey: string) => {
        this.selectedPermissions[actionKey] = true;
      });

      // Select features
      modulePermissions.features?.forEach((featureKey: string) => {
        this.selectedPermissions[featureKey] = true;

        // Auto-select all actions for the feature
        const group = Object.values(this.groupedPermissions).find(g => g.module === moduleKey);
        const feature = group?.features?.find(f => f.key === featureKey);
        
        feature?.actions?.forEach(action => {
          this.selectedPermissions[action.key] = true;
        });
      });
    });

    this.selectedTemplate = templateKey;
    this.toast.success(`Applied template: ${template.name}`, 'Template Applied');
  }

  /**
   * Select all available permissions (for Administrator template)
   */
  private selectAllPermissions() {
    Object.values(this.groupedPermissions).forEach(group => {
      // Enable module
      this.enabledModules[group.module] = true;

      // Select all module actions
      group.actions?.forEach(action => {
        this.selectedPermissions[action.key] = true;
      });

      // Select all features and their actions
      group.features?.forEach(feature => {
        this.selectedPermissions[feature.key] = true;
        feature.actions?.forEach(action => {
          this.selectedPermissions[action.key] = true;
        });
      });
    });
  }

  /**
   * Clear template selection
   */
  clearTemplate() {
    this.selectedTemplate = ''; // Empty string for dropdown
    this.selectedPermissions = {};
    this.enabledModules = {};
    this.role.role_name = '';
    this.role.role_key = '';
    this.role.description = '';
  }

  /* ================= SELECT ALL MODULE ACTIONS ================= */

  /**
   * Check if all module-level actions are selected
   */
  areModuleActionsFullySelected(group: PermissionGroup): boolean {
    if (!group.actions || group.actions.length === 0) return false;
    return group.actions.every(action => this.selectedPermissions[action.key]);
  }

  /**
   * Toggle all module-level actions
   */
  toggleAllModuleActions(group: PermissionGroup, event: any) {
    const isChecked = event.target.checked;
    if (!group.actions) return;

    group.actions.forEach(action => {
      this.selectedPermissions[action.key] = isChecked;
    });
  }


}

export class Role {
  role_id!: number;
  role_name: string = "";
  description: string = "";
  permissions: any[] = [];
  client_id: number = 0;
  status: string = "Active";
  role_key: string = "";
}

interface UiPermission {
  key: string;
  label: string;
  module: string;
  isFeature: boolean;
  featureName?: string;
  level: string; // Dynamic level from API
  featureLevelLabel?: string; // e.g., "Parent Level", "Child Level"
  actions?: PermissionAction[];
}

interface PermissionAction {
  key: string;
  label: string;
  actionName: string; // e.g., 'create', 'view', 'update', 'delete'
  level?: string;
}

interface PermissionGroup {
  module: string;
  moduleLabel: string;
  level: 'module';
  levelLabel: string; // e.g., "Parent Level"
  actions: PermissionAction[];
  features: UiPermission[];
}
